import React from 'react';
import { Loader2, Calendar, RefreshCw } from 'lucide-react';
import type { IntegrationInfo } from '../../../lib/http';

interface Props {
  integration?: IntegrationInfo;
  isLoading: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onSync: () => void;
  onSelectCalendar: () => void;
  isDisconnecting: boolean;
  isSyncing: boolean;
}

const GoogleCalendarCard: React.FC<Props> = ({
  integration,
  isLoading,
  onConnect,
  onDisconnect,
  onSync,
  onSelectCalendar,
  isDisconnecting,
  isSyncing,
}) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="h-4 bg-gray-100 rounded w-2/3" />
      </div>
    );
  }

  const isConnected = integration?.connected;
  const needsAttention = integration?.needs_attention;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          {/* Google Calendar Icon */}
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
            <Calendar className="w-7 h-7 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Google Calendar</h3>
            <p className="text-sm text-gray-600">
              Two-way sync bookings with your Google Calendar
            </p>
          </div>
        </div>

        {/* Status Badge */}
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
          isConnected && !needsAttention
            ? 'bg-green-100 text-green-700'
            : needsAttention
            ? 'bg-amber-100 text-amber-700'
            : 'bg-gray-100 text-gray-600'
        }`}>
          {needsAttention ? 'Needs Attention' : isConnected ? 'Connected' : 'Not Connected'}
        </div>
      </div>

      {/* Connected State */}
      {isConnected && !needsAttention && (
        <div className="mt-6 space-y-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>
              Syncing to: <strong>{integration?.calendar_id || 'Primary Calendar'}</strong>
            </span>
          </div>

          {integration?.last_synced_at && (
            <div className="text-xs text-gray-500">
              Last synced: {new Date(integration.last_synced_at).toLocaleString()}
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={onSelectCalendar}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Change Calendar
            </button>
            <button
              onClick={onSync}
              disabled={isSyncing}
              className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSyncing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              {isSyncing ? 'Syncing...' : 'Sync Now'}
            </button>
            <button
              onClick={onDisconnect}
              disabled={isDisconnecting}
              className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 transition-colors disabled:opacity-50"
            >
              {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
            </button>
          </div>
        </div>
      )}

      {/* Needs Attention State */}
      {needsAttention && (
        <div className="mt-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-amber-800">
              {integration?.error_message || 'Your Google Calendar connection needs attention.'}
            </p>
          </div>
          <button
            onClick={onConnect}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
          >
            Reconnect Google Calendar
          </button>
        </div>
      )}

      {/* Disconnected State */}
      {!isConnected && !needsAttention && (
        <div className="mt-6">
          <button
            onClick={onConnect}
            className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Connect Google Calendar
          </button>
          <p className="mt-3 text-sm text-gray-500">
            Bookings will automatically sync to your Google Calendar. External events will block availability.
          </p>
        </div>
      )}
    </div>
  );
};

export default GoogleCalendarCard;
